import { useQuery } from '@tanstack/react-query';

import { PREFIX_URL } from '@/src/api/config';
import { axios } from '@/src/lib/axios/axios';
import { ExtractFnReturnType, QueryConfig, queryClient } from '@/src/lib/react-query/react-query';
import { BASE_PROJECT_PATH, generateRoutePath } from '@/src/routes';

import { INBOX_API_KEY } from './config';

import { InboxEntryStatus } from '../types';

const url = `${PREFIX_URL}${BASE_PROJECT_PATH}/inbox/count`;

type FilterCountersResponse = {
  data: number;
};

export const getFilterCounters = ({ projectId, status }): Promise<FilterCountersResponse> => {
  return axios.get(generateRoutePath(url, { projectId: projectId }), {
    params: {
      status,
    },
    showError: false,
  });
};

export const filterCountersKeys = {
  all: ['filterCounters'] as const,
  list: [INBOX_API_KEY, 'filterCounters'] as const,
  lists: (projectId: string) => [...filterCountersKeys.list, projectId] as const,
  detail: (status: InboxEntryStatus, projectId: string) =>
    [...filterCountersKeys.lists(projectId), status] as const,
};

type QueryFnType = typeof getFilterCounters;

type useFilterCountersOptions = {
  projectId: string;
  status: InboxEntryStatus;
  config?: QueryConfig<QueryFnType>;
};

export const useFilterCounters = ({ projectId, status, config }: useFilterCountersOptions) => {
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    queryKey: [...filterCountersKeys.list, projectId, status],
    queryFn: () => getFilterCounters({ projectId, status }),
    staleTime: Infinity,
    ...config,
  });
};

const getCounterOffsetFromNewStatus = (status) => {
  if (status === InboxEntryStatus.Closed) {
    return {
      openOffset: -1,
      newOffset: -1,
    };
  }

  if (status === InboxEntryStatus.Open) {
    return {
      openOffset: 1,
      newOffset: -1,
    };
  }
  if (status === InboxEntryStatus.New) {
    return {
      openOffset: -1,
      newOffset: 1,
    };
  }

  return {
    openOffset: 0,
    newOffset: 0,
  };
};

export const updateFilterStatusCounters = ({ projectId, newStatus }) => {
  const { openOffset, newOffset } = getCounterOffsetFromNewStatus(newStatus);

  queryClient.setQueryData(
    [...filterCountersKeys.detail(InboxEntryStatus.Open, projectId)],
    (prev: any) => {
      return {
        ...prev,
        data: prev.data + openOffset,
      };
    }
  );
  queryClient.setQueryData(
    [...filterCountersKeys.detail(InboxEntryStatus.New, projectId)],
    (prev: any) => {
      return {
        ...prev,
        data: prev.data + newOffset,
      };
    }
  );
};
